import React, { useState } from "react";
import {
  Plus,
  Users,
  Edit,
  Trash2,
  Phone,
  Bike,
  CreditCard,
  Search,
  Filter,
  UserCheck,
  UserX,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useAgents } from "../hooks/useAgents";
import "../styles/Home.css";

const Agents = () => {
  // Use real API integration
  const {
    agents: apiAgents,
    loading,
    error,
    createAgent,
    updateAgent,
    deleteAgent,
    refreshAgents,
    clearError,
    isAuthenticated,
  } = useAgents();

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Transform API agents to match component format
  const agents = apiAgents.map((agent) => ({
    id: agent._id,
    name: agent.fullName,
    phone: agent.phoneNumber,
    bikeNumber: agent.vehicleNumber,
    aadharNumber: agent.aadharNumber,
    status: agent.isActive ? agent.status : "inactive",
    joinDate: new Date(agent.createdAt).toLocaleDateString("en-US", {
      month: "short",
      year: "2-digit",
    }),
    totalDeliveries: agent.totalDeliveries || 0,
    rating: agent.rating || 0,
    profileImage: agent.profileImage,
    currentLocation: agent.currentLocation,
    // Keep original API fields for updates
    _id: agent._id,
    originalAgent: agent,
  }));

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    bikeNumber: "",
    aadharNumber: "",
  });

  const [formErrors, setFormErrors] = useState({});

  const statusOptions = [
    { value: "all", label: "All Agents", color: "secondary" },
    { value: "active", label: "Active", color: "success" },
    { value: "busy", label: "Busy", color: "warning" },
    { value: "inactive", label: "Inactive", color: "danger" },
  ];

  const filteredAgents = agents.filter((agent) => {
    const matchesSearch =
      agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.phone.includes(searchTerm) ||
      agent.bikeNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agent.aadharNumber.includes(searchTerm);
    const matchesStatus =
      statusFilter === "all" || agent.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    let processedValue = value;

    // Auto-format Aadhar number
    if (name === "aadharNumber") {
      // Remove all non-digit characters
      const digits = value.replace(/\D/g, "");

      // Limit to 12 digits
      const limitedDigits = digits.slice(0, 12);

      // Auto-format with spaces (XXXX XXXX XXXX)
      if (limitedDigits.length > 8) {
        processedValue = `${limitedDigits.slice(0, 4)} ${limitedDigits.slice(
          4,
          8
        )} ${limitedDigits.slice(8)}`;
      } else if (limitedDigits.length > 4) {
        processedValue = `${limitedDigits.slice(0, 4)} ${limitedDigits.slice(
          4
        )}`;
      } else {
        processedValue = limitedDigits;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (
      !/^[+]?[0-9\s\-()]{10,15}$/.test(formData.phone.replace(/\s/g, ""))
    ) {
      errors.phone = "Please enter a valid phone number";
    }

    // Bike number is optional for admin - no validation required
    // Admin can add agents without vehicle details if needed

    if (!formData.aadharNumber.trim()) {
      errors.aadharNumber = "Aadhar number is required";
    } else {
      // Remove spaces and dashes for validation
      const cleanAadhar = formData.aadharNumber.replace(/[\s-]/g, "");

      // Validate Aadhar number format (12 digits)
      if (!/^[0-9]{12}$/.test(cleanAadhar)) {
        errors.aadharNumber = "Aadhar number must be exactly 12 digits";
      } else {
        // Additional Aadhar validation - simple checksum validation
        // Aadhar uses Verhoeff algorithm, but for basic validation we'll check patterns
        if (/^([0-9])\1{11}$/.test(cleanAadhar)) {
          errors.aadharNumber =
            "Invalid Aadhar number - all digits cannot be the same";
        } else if (cleanAadhar === "000000000000") {
          errors.aadharNumber = "Invalid Aadhar number";
        }
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Transform form data to API format
      const agentData = {
        fullName: formData.name,
        phoneNumber: formData.phone,
        vehicleNumber: formData.bikeNumber,
        aadharNumber: formData.aadharNumber,
        // Add default values for required fields
        profileImage: "",
        licenseImage: "",
        aadharImage: "",
        currentLocation: {
          latitude: 0,
          longitude: 0,
          address: "Not specified",
        },
      };

      await createAgent(agentData);

      setFormData({ name: "", phone: "", bikeNumber: "", aadharNumber: "" });
      setFormErrors({});
      setShowAddForm(false);
    } catch (error) {
      console.error("Failed to create agent:", error);
      alert("Failed to create agent. Please try again.");
    }
  };

  const handleEditAgent = (agent) => {
    setEditingAgent(agent);
    setFormData({
      name: agent.name,
      phone: agent.phone,
      bikeNumber: agent.bikeNumber,
      aadharNumber: agent.aadharNumber,
    });
    setFormErrors({});
    setShowEditForm(true);
  };

  const handleUpdateAgent = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Transform form data to API format
      const updateData = {
        fullName: formData.name,
        phoneNumber: formData.phone,
        vehicleNumber: formData.bikeNumber,
        aadharNumber: formData.aadharNumber,
      };

      await updateAgent(editingAgent._id, updateData);

      setFormData({ name: "", phone: "", bikeNumber: "", aadharNumber: "" });
      setFormErrors({});
      setShowEditForm(false);
      setEditingAgent(null);
    } catch (error) {
      console.error("Failed to update agent:", error);
      alert("Failed to update agent. Please try again.");
    }
  };

  const handleDeleteAgent = async (id) => {
    if (window.confirm("Are you sure you want to delete this agent?")) {
      try {
        await deleteAgent(id);
      } catch (error) {
        console.error("Failed to delete agent:", error);
        alert("Failed to delete agent. Please try again.");
      }
    }
  };

  const handleToggleStatus = async (agent) => {
    try {
      const newStatus = agent.status === "active" ? "inactive" : "active";
      const updateData = {
        status: newStatus,
        isActive: newStatus === "active",
      };

      await updateAgent(agent._id || agent.id, updateData);
      refreshAgents(); // Refresh the list to show updated status
    } catch (error) {
      console.error("Failed to toggle agent status:", error);
      alert("Failed to update agent status. Please try again.");
    }
  };

  const handleCloseForms = () => {
    setShowAddForm(false);
    setShowEditForm(false);
    setEditingAgent(null);
    setFormData({ name: "", phone: "", bikeNumber: "", aadharNumber: "" });
    setFormErrors({});
  };

  const getStatusBadge = (status) => {
    const baseClasses =
      "badge d-inline-flex align-items-center gap-1 px-2 py-1";

    switch (status) {
      case "active":
        return (
          <span className={`${baseClasses} bg-success text-white`}>
            <UserCheck size={10} />
            Active
          </span>
        );
      case "busy":
        return (
          <span className={`${baseClasses} bg-warning text-dark`}>
            <Users size={10} />
            Busy
          </span>
        );
      case "inactive":
      default:
        return (
          <span className={`${baseClasses} bg-danger text-white`}>
            <UserX size={10} />
            Inactive
          </span>
        );
    }
  };

  return (
    <div>
      <div className="sticky-top bg-white border-bottom shadow-sm">
        <div className="container-fluid d-flex justify-content-between align-items-center py-3 px-3">
          <h5 className="mb-0">
            <Users className="me-2" size={20} />
            Delivery Agents Management
          </h5>
          <div className="d-flex gap-2">
            <button
              className="btn btn-outline-primary"
              onClick={refreshAgents}
              disabled={loading}
            >
              <RefreshCw
                size={16}
                className={`me-2 ${loading ? "spinning" : ""}`}
              />
              Refresh
            </button>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddForm(true)}
            >
              <Plus className="me-2" size={16} />
              Add Agent
            </button>
          </div>
        </div>
      </div>

      <div className="container-fluid py-4 sticky-content">
        {/* Authentication Check */}
        {!isAuthenticated && (
          <div className="alert alert-warning mx-3" role="alert">
            <AlertCircle size={20} className="me-2" />
            Please sign in to view agents
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div
            className="alert alert-danger d-flex justify-content-between align-items-center mx-3"
            role="alert"
          >
            <div>
              <AlertCircle size={20} className="me-2" />
              {error}
            </div>
            <button
              type="button"
              className="btn btn-outline-danger btn-sm"
              onClick={clearError}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading agents...</span>
            </div>
            <p className="text-muted mt-2">Loading agents...</p>
          </div>
        )}
        {/* Search and Filter */}
        <div className="row mb-4 mx-3">
          <div className="col-md-8">
            <div className="row">
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Search size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search agents by name, phone, bike number, or Aadhar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="input-group">
                  <span className="input-group-text">
                    <Filter size={16} />
                  </span>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Agents List */}
        <div className="mx-3">
          {filteredAgents.length === 0 ? (
            <div className="card bg-white shadow-sm">
              <div className="card-body text-center py-5">
                <Users size={64} className="text-muted mb-3" />
                <h5 className="text-muted">No agents found</h5>
                <p className="text-muted">
                  Click "Add Agent" to start adding delivery agents
                </p>
              </div>
            </div>
          ) : (
            <div className="row g-4">
              {filteredAgents.map((agent) => (
                <div
                  key={agent.id}
                  className="col-xl-4 col-lg-6 col-md-6 col-sm-12"
                >
                  <div className="card agent-card h-100">
                    <div className="card-header">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 className="mb-1 fw-bold text-primary">
                            {agent.name}
                          </h6>
                          <small className="text-muted">ID: {agent.id}</small>
                        </div>
                        {getStatusBadge(agent.status)}
                      </div>
                    </div>

                    <div className="card-body">
                      <div className="text-center mb-3">
                        {agent.profileImage ? (
                          <img
                            src={agent.profileImage}
                            alt={agent.name}
                            className="agent-avatar"
                          />
                        ) : (
                          <div className="agent-avatar">
                            {agent.name.slice(0, 2).toUpperCase()}
                          </div>
                        )}
                      </div>

                      <div className="agent-details">
                        <div className="detail-item">
                          <Phone size={14} className="detail-icon" />
                          <div className="detail-content">
                            <small className="detail-label">Phone</small>
                            <span className="detail-value">{agent.phone}</span>
                          </div>
                        </div>

                        <div className="detail-item">
                          <Bike size={14} className="detail-icon" />
                          <div className="detail-content">
                            <small className="detail-label">Vehicle</small>
                            <span className="detail-value">
                              {agent.bikeNumber}
                            </span>
                          </div>
                        </div>

                        <div className="detail-item">
                          <CreditCard size={14} className="detail-icon" />
                          <div className="detail-content">
                            <small className="detail-label">Aadhar</small>
                            <span className="detail-value">
                              {agent.aadharNumber}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="agent-stats">
                        <div className="stat-item">
                          <div className="stat-value">
                            {agent.totalDeliveries}
                          </div>
                          <div className="stat-label">Deliveries</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">
                            <span className="rating-badge">
                              ⭐ {agent.rating.toFixed(1)}
                            </span>
                          </div>
                          <div className="stat-label">Rating</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{agent.joinDate}</div>
                          <div className="stat-label">Joined</div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer">
                      <div className="toggle-container">
                        <div className="toggle-label">
                          {agent.status === "active" ? (
                            <UserCheck size={16} className="text-success" />
                          ) : (
                            <UserX size={16} className="text-muted" />
                          )}
                          Status
                          <span
                            className={`status-text ${
                              agent.status === "active"
                                ? "status-active"
                                : "status-inactive"
                            }`}
                          >
                            {agent.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={agent.status === "active"}
                            onChange={() => handleToggleStatus(agent)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                      </div>
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-outline-secondary btn-sm flex-fill"
                          onClick={() => handleEditAgent(agent)}
                        >
                          <Edit size={14} className="me-1" />
                          Edit
                        </button>
                        <button
                          className="btn btn-outline-danger btn-sm flex-fill"
                          onClick={() => handleDeleteAgent(agent.id)}
                        >
                          <Trash2 size={14} className="me-1" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Agent Modal */}
      {showAddForm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Users className="me-2" size={20} />
                  Add New Delivery Agent
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseForms}
                ></button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="name" className="form-label">
                        <Users className="me-2" size={16} />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.name ? "is-invalid" : ""
                        }`}
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter agent's full name"
                      />
                      {formErrors.name && (
                        <div className="invalid-feedback">
                          {formErrors.name}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone" className="form-label">
                        <Phone className="me-2" size={16} />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        className={`form-control ${
                          formErrors.phone ? "is-invalid" : ""
                        }`}
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                      />
                      {formErrors.phone && (
                        <div className="invalid-feedback">
                          {formErrors.phone}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="bikeNumber" className="form-label">
                        <Bike className="me-2" size={16} />
                        Vehicle Number{" "}
                        <small className="text-muted">(Optional)</small>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.bikeNumber ? "is-invalid" : ""
                        }`}
                        id="bikeNumber"
                        name="bikeNumber"
                        value={formData.bikeNumber}
                        onChange={handleInputChange}
                        placeholder="MH01-AB-1234 (Optional)"
                        style={{ textTransform: "uppercase" }}
                      />
                      {formErrors.bikeNumber && (
                        <div className="invalid-feedback">
                          {formErrors.bikeNumber}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="aadharNumber" className="form-label">
                        <CreditCard className="me-2" size={16} />
                        Aadhar Number *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.aadharNumber ? "is-invalid" : ""
                        }`}
                        id="aadharNumber"
                        name="aadharNumber"
                        value={formData.aadharNumber}
                        onChange={handleInputChange}
                        placeholder="123456789012 (12 digits)"
                        maxLength="14"
                      />
                      <small className="form-text text-muted">
                        Enter 12-digit Aadhar number (spaces and dashes are
                        optional)
                      </small>
                      {formErrors.aadharNumber && (
                        <div className="invalid-feedback">
                          {formErrors.aadharNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseForms}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <Plus className="me-2" size={16} />
                    Add Agent
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Agent Modal */}
      {showEditForm && editingAgent && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Edit className="me-2" size={20} />
                  Edit Delivery Agent
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseForms}
                ></button>
              </div>
              <form onSubmit={handleUpdateAgent}>
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label htmlFor="editName" className="form-label">
                        <Users className="me-2" size={16} />
                        Full Name *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.name ? "is-invalid" : ""
                        }`}
                        id="editName"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter agent's full name"
                      />
                      {formErrors.name && (
                        <div className="invalid-feedback">
                          {formErrors.name}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="editPhone" className="form-label">
                        <Phone className="me-2" size={16} />
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        className={`form-control ${
                          formErrors.phone ? "is-invalid" : ""
                        }`}
                        id="editPhone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="+91 98765 43210"
                      />
                      {formErrors.phone && (
                        <div className="invalid-feedback">
                          {formErrors.phone}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="editBikeNumber" className="form-label">
                        <Bike className="me-2" size={16} />
                        Vehicle Number{" "}
                        <small className="text-muted">(Optional)</small>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.bikeNumber ? "is-invalid" : ""
                        }`}
                        id="editBikeNumber"
                        name="bikeNumber"
                        value={formData.bikeNumber}
                        onChange={handleInputChange}
                        placeholder="MH01-AB-1234 (Optional)"
                        style={{ textTransform: "uppercase" }}
                      />
                      {formErrors.bikeNumber && (
                        <div className="invalid-feedback">
                          {formErrors.bikeNumber}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label htmlFor="editAadharNumber" className="form-label">
                        <CreditCard className="me-2" size={16} />
                        Aadhar Number *
                      </label>
                      <input
                        type="text"
                        className={`form-control ${
                          formErrors.aadharNumber ? "is-invalid" : ""
                        }`}
                        id="editAadharNumber"
                        name="aadharNumber"
                        value={formData.aadharNumber}
                        onChange={handleInputChange}
                        placeholder="123456789012 (12 digits)"
                        maxLength="14"
                      />
                      <small className="form-text text-muted">
                        Enter 12-digit Aadhar number (spaces and dashes are
                        optional)
                      </small>
                      {formErrors.aadharNumber && (
                        <div className="invalid-feedback">
                          {formErrors.aadharNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCloseForms}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <Edit className="me-2" size={16} />
                    Update Agent
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agents;
